import { Box } from '@pets-ui/Box/Box'
import { HStack } from '@pets-ui/HStack/HStack'
import { IconButton } from '@pets-ui/IconButton/IconButton'
import { ArrowRightSmall, GiftOutlined } from '@pets-ui/icons'
import { type TextProps } from '@pets-ui/Text/Text'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import * as stylex from '@stylexjs/stylex'
import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import cartUiI18nInstance from '../i18n/i18nConfig'

const rippleAnimation = stylex.keyframes({
  '0%': {
    boxShadow: '0 0 0 0 rgba(255, 83, 83, 0.1)',
  },
  '75%': {
    boxShadow: '0 0 0 4px rgba(255, 83, 83, 0.5)',
  },
  '100%': {
    boxShadow: '0 0 0 6px rgba(255, 255, 255, 0)',
  },
})

const styles = stylex.create({
  rippleDot: {
    borderRadius: '50%',
    alignItems: 'center',
    animationDuration: '1s',
    animationIterationCount: 'infinite',
    animationName: rippleAnimation,
    animationTimingFunction: 'ease-out',
    backgroundColor: 'rgba(255, 83, 83, 0.6)',
    display: 'flex',
    justifyContent: 'center',
    height: '12px',
    width: '12px',
  },
  giftIcon: {
    color: colors.red600,
  },
  giftIconContainer: {
    borderRadius: '50%',
    alignItems: 'center',
    backgroundColor: colors.red100,
    justifyContent: 'center',
    height: '20px',
    width: '20px',
  },
  absoluteLayer: {
    transition: 'opacity 0.2s ease-out',
  },
  selectedTitleBar: {
    backgroundColor: colors.blueGrey50,
  },
  unselectedTitleBar: {
    backgroundColor: colors.red100,
  },
  selectedTitleBarBase: {
    padding: 6,
    gap: 4,
    justifyContent: 'space-between',
    width: '100%',
  },
})
export interface GiveawaySelectTitleProps extends Omit<TextProps, 'title'> {
  title: ReactNode
  totalSelected: number
  isAllSelected: boolean
}

export const GiveawaySelectTitle = (props: GiveawaySelectTitleProps) => {
  const { title, isAllSelected, totalSelected, ...rest } = props
  const { t } = useTranslation('cartUi', { i18n: cartUiI18nInstance })

  return (
    <HStack gap={1} width="100%" rootStyle={[styles.selectedTitleBarBase, isAllSelected ? styles.selectedTitleBar : styles.unselectedTitleBar]}>
      <HStack flex={1} alignItems="center" gap={isAllSelected ? 1 : 1.5} flexWrap="nowrap">
        {isAllSelected && (
          <HStack rootStyle={styles.giftIconContainer}>
            <GiftOutlined fontSize={12} rootStyle={styles.giftIcon} />
          </HStack>
        )}
        <HStack width={isAllSelected ? 0 : 20} height={20} alignItems="center" justifyContent="center">
          <HStack rootStyle={styles.rippleDot} opacity={isAllSelected ? 0 : 1} width={isAllSelected ? 0 : 12}>
            <Box
              display="flex"
              backgroundColor={colors.white}
              borderColor="rgba(255, 83, 83, 0.6)"
              borderRadius="50%"
              borderStyle="solid"
              borderWidth="1px"
              width={8}
              height={8}
            />
          </HStack>
        </HStack>
        <Text
          variant="caption"
          lineClamp={1}
          onClick={(e) => {
            const selection = window.getSelection()
            // NOTE: 如果有選擇文字，阻止事件冒泡
            if (selection && selection.toString().length > 0) {
              e.preventDefault()
              e.stopPropagation()
            }
          }}
          color={colors.grey800}
          {...rest}
        >
          {title}
        </Text>
      </HStack>
      <HStack gap={1}>
        {!isAllSelected && (
          <Text color={colors.red600} {...rest}>
            {t('cartUi.chooseGiveaway', { defaultValue: '選贈品' })}
          </Text>
        )}
        <IconButton fontSize={14}>
          <ArrowRightSmall color={isAllSelected ? colors.grey600 : colors.red600} />
        </IconButton>
      </HStack>
    </HStack>
  )
}
